import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { CheckCircleIcon, CheckIcon, ExclamationTriangleIcon } from '@heroicons/react/16/solid'
import type { DraftState, SubmitReview } from '../../../Model/OrderWizard/OrderWizardDraft.ts'
import type { SequencingPlatformKey } from '../../../Generated/SequencingPlatform.ts'
import { duplicateNames, reviewSamples, trimmedOrEdna } from '../../../Services/OrderWizard/submission.ts'
import type { IssueList } from '../../../Services/OrderWizard/issues.ts'
import { SubmitSequencingPlatform } from '../OrderManagementPage/Modals/SubmitSequencingPlatform.tsx'
import { plural } from '../../../Util/plural.ts'
import { trackWizardContinuedInClassic } from '../../../Services/OrderWizard/telemetry.ts'
import { Callout } from './Primitives.tsx'
import { OrderSettingsSection } from './DiscoverySummaryView.tsx'
import { RETRY_BUTTON } from './retryStyle.ts'
import { lengthWarning } from '../../../Services/OrderWizard/lengthCheck.ts'

/** The settings the order goes in with, still changeable here: Files is often skipped once the order exists. */
export const SettingsSummary = ({ draft }: { draft: DraftState }) =>
  draft.settings ? (
    <OrderSettingsSection
      settings={draft.settings}
      platform={draft.discovery && draft.discovery.platform !== 'UNKNOWN' ? draft.discovery.platform : null}
      edit={draft}
      draft={draft}
    />
  ) : null

/**
 * After submit, metadata is what is left: it stays editable on the order (maps and comparisons read it
 * live), so the confirmation leads to the classic order view, where it is entered.
 */
export const SubmittedNotice = ({ orderId }: { orderId: string }) => (
  <section className="space-y-3 rounded-lg border border-slate-200 p-3" data-testid="wizard-submitted">
    <p className="flex items-center gap-1.5 font-semibold text-slate-900">
      <CheckCircleIcon className="h-4 w-4 text-green-600" /> The order was submitted to processing.
    </p>
    <div className="space-y-1 rounded-lg bg-sky-50 p-3" data-testid="wizard-metadata-nudge">
      <p className="font-medium text-slate-900">Add metadata now — needed for maps and comparisons</p>
      <p className="text-sm text-slate-600">What matters: coordinates, sampling date, custom fields.</p>
      <Link
        to={`/order/${orderId}`}
        className="btn btn-primary btn-sm"
        data-testid="wizard-metadata-now"
        onClick={() => trackWizardContinuedInClassic('submitted-metadata')}
      >
        Add metadata in the order view
      </Link>
      <p className="text-xs text-slate-500">
        There, “Update Sample Metadata” takes a sheet, and “Edit Data” (where offered) fills it in a grid. The order view also shows
        the processing.
      </p>
    </div>
  </section>
)

export const WarningNote = ({ issues, onShowIssues }: { issues: IssueList; onShowIssues: () => void }) =>
  issues.warnings.length > 0 && (
    <p className="text-sm text-slate-700" data-testid="wizard-review-warnings">
      <ExclamationTriangleIcon className="mr-1 inline h-3.5 w-3.5 align-[-2px] text-amber-500" />
      {plural(issues.warnings.length, 'issue')} to check before submitting —{' '}
      <button type="button" className="link" onClick={onShowIssues}>
        see the list
      </button>
      .
    </p>
  )

/** Why submit is not possible yet, with the way to the issues. */
export const BlockedReason = ({ reason, issues, onShowIssues }: { reason: string; issues: IssueList; onShowIssues: () => void }) => (
  <span className="text-sm text-slate-600" data-testid="wizard-submit-blocked">
    {reason}
    {issues.blockers.length > 0 && (
      <>
        {' — '}
        <button type="button" className="link" onClick={onShowIssues}>
          show them
        </button>
      </>
    )}
  </span>
)

/** Before the review: what the order goes in with, the check under way, and what is in the way. */
export const ReviewGate = ({
  draft,
  issues,
  checking,
  failure,
  refusal,
  blockedReason,
  checked = false,
  onRetry,
  onShowIssues
}: {
  draft: DraftState
  issues: IssueList
  checking: boolean
  /** A check has answered: something fixed outside the wizard needs it run again. */
  checked?: boolean
  /** Why the check could not run. */
  failure: string | undefined
  /** What the classic checks refused; the issue list carries it as a blocker. */
  refusal: string | undefined
  blockedReason: string | undefined
  onRetry: () => void
  onShowIssues: () => void
}) => (
  <section className="space-y-2" data-testid="wizard-review">
    <SettingsSummary draft={draft} />
    <WarningNote issues={issues} onShowIssues={onShowIssues} />
    {failure && (
      <p className="text-sm text-error" data-testid="wizard-review-failed">
        {failure}{' '}
        <button type="button" className={RETRY_BUTTON} onClick={onRetry} data-testid="wizard-review-retry">
          Try again
        </button>
      </p>
    )}
    {refusal && !blockedReason && <p className="text-sm text-error">{refusal}</p>}
    {(checking || blockedReason || checked) && (
      <div className="flex flex-wrap items-center gap-2">
        {checking && (
          <span className="flex items-center gap-1.5 text-sm text-slate-600" data-testid="wizard-review-checking">
            <span className="loading loading-spinner loading-xs" /> Checking…
          </span>
        )}
        {blockedReason && <BlockedReason reason={blockedReason} issues={issues} onShowIssues={onShowIssues} />}
        {checked && !checking && <CheckAgain onClick={onRetry} />}
      </div>
    )}
  </section>
)

/** Runs the checks again for a draft that has not changed, after a fix made in the classic view. */
export const CheckAgain = ({ onClick }: { onClick: () => void }) => (
  <button type="button" className={RETRY_BUTTON} data-testid="wizard-review-check-again" onClick={onClick}>
    Check again
  </button>
)

/** Read lengths that do not fit the chosen primer pair or the samples' length filter, with the way to the fix. */
export const ReadLengthWarning = ({
  review,
  orderId,
  onCheckPrimers
}: {
  review: SubmitReview
  orderId: string
  onCheckPrimers: () => void
}) => {
  const warning = review.lengthCheck && lengthWarning(review.lengthCheck, review.primer)
  if (!warning) {
    return null
  }
  return (
    <Callout className="space-y-1 rounded p-2 text-sm text-slate-700" data-testid="wizard-review-read-length">
      <p>
        <ExclamationTriangleIcon className="mr-1 inline h-3.5 w-3.5 align-[-2px] text-amber-500" />
        {warning}
      </p>
      {review.primer === 'SKIPPED' ? (
        <Link to={`/order/${orderId}`} className="link text-xs" onClick={() => trackWizardContinuedInClassic('review')}>
          Adjust the amplicon min/max length in the classic view
        </Link>
      ) : (
        <button type="button" className={RETRY_BUTTON} data-testid="wizard-review-read-length-primers" onClick={onCheckPrimers}>
          Check the primers
        </button>
      )}
    </Callout>
  )
}

/** What the checks found: the primers, the previews, the platform and the cost. */
export const ReviewDetails = ({
  review,
  orderId,
  warningNote,
  overrideInFlight,
  onOverride,
  earlyAccessAcknowledged,
  onEarlyAccessAcknowledged,
  onCheckPrimers
}: {
  review: SubmitReview
  orderId: string
  warningNote: ReactNode
  overrideInFlight: boolean
  onOverride: (platform: SequencingPlatformKey | null) => void
  earlyAccessAcknowledged: boolean
  onEarlyAccessAcknowledged: (acknowledged: boolean) => void
  onCheckPrimers: () => void
}) => {
  const order = review.order
  const measured = review.lengthCheck?.measured ? review.lengthCheck.written : undefined
  return (
    <>
      {review.primer === 'SKIPPED' ? (
        <Callout className="rounded p-2 text-sm text-slate-700">
          Primer validation was skipped as the samples are already trimmed. spcfy cannot check that the primers fit the target
          {order.targetTaxonomy ? ` (${order.targetTaxonomy})` : ''}, and length filtering uses{' '}
          {measured ? `the read lengths measured on your files (${measured.min}–${measured.max} bp)` : 'a best guess'}.
        </Callout>
      ) : (
        <p className="flex items-center gap-1.5 text-sm text-slate-700">
          <CheckIcon className="h-4 w-4 shrink-0 text-slate-400" /> Primers recognised: {review.primer.forwardName} /{' '}
          {review.primer.reverseName} · {review.primer.reference} · {review.primer.basePairLength} bp
        </p>
      )}
      <ReadLengthWarning review={review} orderId={orderId} onCheckPrimers={onCheckPrimers} />
      {trimmedOrEdna(review) && (
        <p className="text-xs text-slate-500">
          To adjust the amplicon min/max length,{' '}
          <Link to={`/order/${orderId}`} className="link" onClick={() => trackWizardContinuedInClassic('review')}>
            continue in the classic view
          </Link>
          .
        </p>
      )}
      {duplicateNames(review) && (
        <p className="text-sm font-medium text-error">A sample name is used more than once; results may be hard to tell apart.</p>
      )}
      {warningNote}
      {review.preview && (
        <p className="text-sm">
          <strong>{review.preview.toProcess}</strong> of {plural(review.preview.total, 'sample')} will be sent to preprocessing
          {review.preview.alreadyProcessed > 0 && ` (${review.preview.alreadyProcessed} already processed and will be skipped)`}.
        </p>
      )}
      {review.platform && (
        <SubmitSequencingPlatform
          preview={review.platform}
          order={order}
          sampleCount={reviewSamples(review).length}
          overrideInFlight={overrideInFlight}
          onOverride={onOverride}
          earlyAccessAcknowledged={earlyAccessAcknowledged}
          onEarlyAccessAcknowledgedChange={onEarlyAccessAcknowledged}
        />
      )}
      <p className="text-sm">
        Preprocessing is free and does not use credits. Costs incur when postprocessing starts.
        {review.creditCost && (
          <>
            {' '}
            Postprocessing will require <strong>{review.creditCost.creditCost} sample credits</strong>; your balance:{' '}
            <strong>{review.creditCost.currentBalance} credits</strong>.
          </>
        )}
      </p>
    </>
  )
}

export const SubmitAgreements = ({
  agreement,
  terms,
  disabled,
  onAgreement,
  onTerms
}: {
  agreement: boolean
  terms: boolean
  /** Until the checks pass. */
  disabled: boolean
  onAgreement: (accepted: boolean) => void
  onTerms: (accepted: boolean) => void
}) => (
  <>
    <label className="flex cursor-pointer items-center gap-2 text-sm">
      <input
        type="checkbox"
        className="checkbox checkbox-primary checkbox-sm"
        checked={agreement}
        disabled={disabled}
        onChange={(event) => onAgreement(event.target.checked)}
        data-testid="wizard-accept-agreement"
      />
      I accept the Data Processing Agreement of spcfy GmbH.
    </label>
    <label className="flex cursor-pointer items-center gap-2 text-sm">
      <input
        type="checkbox"
        className="checkbox checkbox-primary checkbox-sm"
        checked={terms}
        disabled={disabled}
        onChange={(event) => onTerms(event.target.checked)}
        data-testid="wizard-accept-terms"
      />
      I accept the Terms &amp; Conditions of spcfy GmbH.
    </label>
  </>
)
